import { useState } from 'react';
import type { ReactNode } from 'react';
import { Checkbox as AntCheckbox, Drawer, InputNumber, Segmented, Switch } from 'antd';

import { Button, Field, Input, Select } from '../../../../components/ui';
import type { FieldControlProps } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import type { KhoaDich } from '../../../../i18n';
import { soVN } from '../../../../lib/dinh-dang';
import {
  CAC_MUC,
  CAC_NGUON,
  CAC_NHOM,
  CAC_QUYET_DINH,
  chuanHoaQuyTac,
  kiemQuyTac,
  quyTacMoi,
  sinhId,
  tachThoiLuong,
} from '../../nghiep-vu/quy-tac-canh-bao';
import type { LoiMayChuQuyTac, NguonAudit, QuyetDinhAudit, TruongQuyTac } from '../../nghiep-vu/quy-tac-canh-bao';
import type { BienHieuLuc, MucDoThongBao, NhomTheo, QuyTacCanhBao } from '../../types';
import { useChuQuyTac } from '../../hooks/useChuQuyTac';

/** Giá trị "mọi quyết định" của ô Segmented — antd không nhận `null`. */
const MOI_QUYET_DINH = 'ANY';

type TruongSo = 'threshold' | 'window_seconds' | 'cooldown_seconds';

/**
 * Ngăn thêm hoặc sửa một quy tắc cảnh báo (ADR-0059 mục 4.1).
 *
 * Quy tắc mới điền sẵn ví dụ của ADR ("20 lần DENY của một user trong 5
 * phút"). Mã sinh từ tên khi thêm, không đổi được khi sửa — audit-service và
 * cảnh báo đã kích hoạt tham chiếu quy tắc bằng mã. Lỗi chỉ hiện sau lần bấm
 * đầu tiên, rồi cập nhật theo từng phím; biên số hiện sẵn dưới ô.
 */
export function NganQuyTacCanhBao({
  open,
  dangSua,
  dsHienCo,
  bien,
  loiMayChu,
  onClose,
  onLuu,
}: {
  open: boolean;
  /** `null` là thêm mới. */
  dangSua: QuyTacCanhBao | null;
  dsHienCo: readonly QuyTacCanhBao[];
  bien: BienHieuLuc | null;
  loiMayChu: LoiMayChuQuyTac[];
  onClose: () => void;
  onLuu: (q: QuyTacCanhBao) => void;
}) {
  const { t, locale } = useT();
  const chu = useChuQuyTac();
  const [ban, setBan] = useState<QuyTacCanhBao>(() => dangSua ?? quyTacMoi());
  const [daThu, setDaThu] = useState(false);

  const fields = bien?.fields ?? null;
  const idKhac = new Set(dsHienCo.filter((q) => q.id !== dangSua?.id).map((q) => q.id));
  const ungVien = chuanHoaQuyTac({ ...ban, id: dangSua ? dangSua.id : sinhId(ban.name, idKhac) });
  const loi = kiemQuyTac(ungVien, fields, idKhac);

  const doi = <K extends keyof QuyTacCanhBao>(k: K, v: QuyTacCanhBao[K]) => setBan((b) => ({ ...b, [k]: v }));

  /** Lỗi của một trường: phía giao diện trước, rồi tới câu của server cho đúng trường đó. */
  const loiCua = (truong: TruongQuyTac): string | undefined => {
    const l = loi[truong];
    if (l && daThu) return chu.loi(l);
    return loiMayChu.find((x) => x.truong === truong)?.cau;
  };
  const loiChungMayChu = loiMayChu.filter((x) => !x.truong || !(x.truong in ungVien));

  const luu = () => {
    setDaThu(true);
    if (Object.keys(loi).length > 0) return;
    onLuu(ungVien);
  };

  const bienSo = (truong: TruongSo, laGiay: boolean): string | undefined => {
    const b = fields?.[truong];
    const coMin = b?.min !== null && b?.min !== undefined;
    const coMax = b?.max !== null && b?.max !== undefined;
    const dinh = (n: number) => (laGiay ? chu.thoiLuong(n) : soVN(n, locale));
    if (coMin && coMax) return t('caiDat.bien.tuDen', { min: dinh(b!.min!), max: dinh(b!.max!) });
    if (coMax) return t('caiDat.bien.den', { max: dinh(b!.max!) });
    if (coMin) return t('caiDat.bien.tu', { min: dinh(b!.min!) });
    return undefined;
  };

  const goiYDanhSach = (khoa: KhoaDich, truong: 'actions' | 'error_codes') => {
    const toiDa = fields?.[truong]?.max_items;
    return toiDa ? `${t(khoa)} ${t('quyTacCanhBao.toiDaMuc', { so: soVN(toiDa, locale) })}` : t(khoa);
  };

  const oSo = (truong: TruongSo, laGiay: boolean) => (
    <Field label={t(`quyTacCanhBao.truong.${truong}` as KhoaDich)} hint={bienSo(truong, laGiay)} error={loiCua(truong)}>
      {(p) => {
        const v = ban[truong];
        const tach = laGiay && Number.isFinite(v) ? tachThoiLuong(v) : null;
        return (
          <span className="flex flex-wrap items-center gap-2">
            <InputNumber
              {...p}
              className="w-36"
              value={Number.isFinite(v) ? v : null}
              onChange={(x) => doi(truong, x === null || x === undefined ? Number.NaN : Number(x))}
            />
            <span className="text-sm text-content-muted">
              {laGiay ? t('quyTacCanhBao.donViGiay') : t('quyTacCanhBao.donViLan')}
            </span>
            {tach && tach.donVi !== 'giay' && (
              <span className="text-xs text-content-muted">{t('quyTacCanhBao.bang', { giaTri: chu.thoiLuong(v) })}</span>
            )}
          </span>
        );
      }}
    </Field>
  );

  const nhom = (tieuDe: string, children: ReactNode) => (
    <fieldset className="min-w-0 space-y-4">
      <legend className="mb-2 text-sm font-medium text-content-primary">{tieuDe}</legend>
      {children}
    </fieldset>
  );

  const oTags = (truong: 'actions' | 'error_codes', p: FieldControlProps, viDu: string) => (
    <Select<string[]>
      {...p}
      mode="tags"
      className="w-full"
      value={ban[truong]}
      open={false}
      suffixIcon={null}
      tokenSeparators={[',', ' ', ';']}
      placeholder={viDu}
      onChange={(v) =>
        doi(
          truong,
          v.map((s) => (truong === 'error_codes' ? s.trim().toUpperCase() : s.trim())).filter(Boolean),
        )
      }
    />
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={dangSua ? t('quyTacCanhBao.suaTieuDe', { ten: dangSua.name }) : t('quyTacCanhBao.themTieuDe')}
      placement="right"
      size={560}
      destroyOnHidden
      footer={
        <div className="flex flex-wrap justify-end gap-3">
          <Button variant="outline" size="md" onClick={onClose}>
            {t('chung.huy')}
          </Button>
          <Button variant="primary" size="md" onClick={luu}>
            {dangSua ? t('quyTacCanhBao.capNhat') : t('quyTacCanhBao.them')}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <p className="text-sm leading-relaxed text-content-secondary">{t('quyTacCanhBao.nganMo')}</p>
        {loiChungMayChu.map((l, i) => (
          <p key={i} role="alert" className="rounded border border-danger-200 bg-danger-50 px-3 py-2.5 text-xs text-danger-700">
            {l.cau}
          </p>
        ))}

        {nhom(
          t('quyTacCanhBao.nhomChung'),
          <>
            <Field label={t('quyTacCanhBao.truong.name')} required error={loiCua('name')}>
              {(p) => (
                <Input
                  {...p}
                  maxLength={100}
                  value={ban.name}
                  placeholder={t('quyTacCanhBao.tenViDu')}
                  onChange={(e) => doi('name', e.target.value)}
                />
              )}
            </Field>
            <Field
              label={t('quyTacCanhBao.truong.id')}
              hint={t(dangSua ? 'quyTacCanhBao.idKhongDoi' : 'quyTacCanhBao.idTuSinh')}
              error={loiCua('id')}
            >
              {(p) => (
                <span id={p.id} aria-describedby={p['aria-describedby']} className="block break-all font-mono text-sm text-content-primary">
                  {ungVien.id}
                </span>
              )}
            </Field>
            <Field label={t('quyTacCanhBao.truong.enabled')}>
              {(p) => (
                <span className="flex items-center gap-2">
                  <Switch id={p.id} checked={ban.enabled} onChange={(v) => doi('enabled', v)} />
                  <span className="text-sm text-content-secondary">{t(ban.enabled ? 'caiDat.bat' : 'caiDat.tat')}</span>
                </span>
              )}
            </Field>
            <Field label={t('quyTacCanhBao.truong.severity')} error={loiCua('severity')}>
              {(p) => (
                <Segmented<MucDoThongBao>
                  id={p.id}
                  value={ban.severity}
                  options={CAC_MUC.map((m) => ({ value: m, label: t(`caiDat.mucDo.${m}` as KhoaDich) }))}
                  onChange={(v) => doi('severity', v)}
                />
              )}
            </Field>
          </>,
        )}

        {nhom(
          t('quyTacCanhBao.nhomDieuKien'),
          <>
            <Field label={t('quyTacCanhBao.truong.decision')} error={loiCua('decision')}>
              {(p) => (
                <Segmented<string>
                  id={p.id}
                  value={ban.decision ?? MOI_QUYET_DINH}
                  options={[
                    { value: MOI_QUYET_DINH, label: t('quyTacCanhBao.moiQuyetDinh') },
                    ...CAC_QUYET_DINH.map((d) => ({ value: d, label: t(`quyTacCanhBao.quyetDinh.${d}` as KhoaDich) })),
                  ]}
                  onChange={(v) => doi('decision', v === MOI_QUYET_DINH ? null : (v as QuyetDinhAudit))}
                />
              )}
            </Field>
            <Field
              label={t('quyTacCanhBao.truong.actions')}
              hint={goiYDanhSach('quyTacCanhBao.actionsGoiY', 'actions')}
              error={loiCua('actions')}
            >
              {(p) => oTags('actions', p, t('quyTacCanhBao.actionsViDu'))}
            </Field>
            <Field
              label={t('quyTacCanhBao.truong.resource_prefix')}
              hint={t('quyTacCanhBao.resourceGoiY')}
              error={loiCua('resource_prefix')}
            >
              {(p) => (
                <Input
                  {...p}
                  maxLength={200}
                  className="font-mono"
                  value={ban.resource_prefix ?? ''}
                  placeholder={t('quyTacCanhBao.resourceViDu')}
                  onChange={(e) => doi('resource_prefix', e.target.value)}
                />
              )}
            </Field>
            <Field label={t('quyTacCanhBao.truong.sources')} hint={t('quyTacCanhBao.sourcesGoiY')} error={loiCua('sources')}>
              {(p) => (
                <div id={p.id} aria-describedby={p['aria-describedby']}>
                  <AntCheckbox.Group
                    value={ban.sources}
                    options={CAC_NGUON.map((n) => ({ value: n, label: t(`quyTacCanhBao.nguon.${n}` as KhoaDich) }))}
                    onChange={(v) => doi('sources', v as NguonAudit[])}
                  />
                </div>
              )}
            </Field>
            <Field
              label={t('quyTacCanhBao.truong.error_codes')}
              hint={goiYDanhSach('quyTacCanhBao.errorCodesGoiY', 'error_codes')}
              error={loiCua('error_codes')}
            >
              {(p) => oTags('error_codes', p, t('quyTacCanhBao.errorCodesViDu'))}
            </Field>
          </>,
        )}

        {nhom(
          t('quyTacCanhBao.nhomNguong'),
          <>
            <Field label={t('quyTacCanhBao.truong.group_by')} error={loiCua('group_by')}>
              {(p) => (
                <Segmented<NhomTheo>
                  id={p.id}
                  value={ban.group_by}
                  options={CAC_NHOM.map((n) => ({ value: n, label: t(`quyTacCanhBao.nhom.${n}` as KhoaDich) }))}
                  onChange={(v) => doi('group_by', v)}
                />
              )}
            </Field>
            {oSo('threshold', false)}
            {oSo('window_seconds', true)}
            {oSo('cooldown_seconds', true)}
            <p className="text-xs leading-relaxed text-content-muted" role="status">
              {Number.isFinite(ungVien.threshold) && Number.isFinite(ungVien.window_seconds)
                ? t('quyTacCanhBao.xemTruoc', { tomTat: chu.nguong(ungVien), dieuKien: chu.dieuKien(ungVien) })
                : null}
            </p>
          </>,
        )}
      </div>
    </Drawer>
  );
}
